import React, { useMemo, useState } from 'react';
import { Grid, CardHeader } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { convertToArborDate } from 'models/time/arbor-date';
import withStyles from '@mui/styles/withStyles';
import { styles } from './report-styles';
import { useQsReportData } from './hooks/useQsReportData';
import { IQsReportProps } from '../../interfaces/report/IQsReport';

function QsReport(props: IQsReportProps) {
  const { classes } = props;
  const {
    units,
    taskTypes,
    rows,
    filters,
    setFilters,
    lastRefreshTs,
    customerNames,
    customerOptions,
    serviceGroupOptions,
  } = useQsReportData();

  const [sortDesc, setSortDesc] = useState(true);

  const visibleRows =
    !filters.taskTypes.length || filters.taskTypes.includes('All')
      ? rows
      : rows.filter(r => filters.taskTypes.includes(r.taskType));

  const sortedRows = useMemo(() => {
    const nonTotal = visibleRows.filter(r => r.taskType !== 'Total');
    nonTotal.sort((a, b) => {
      const av = a.counts.Total ?? 0;
      const bv = b.counts.Total ?? 0;
      return sortDesc ? bv - av : av - bv;
    });
    const totalRow = visibleRows.find(r => r.taskType === 'Total');
    return totalRow ? [...nonTotal, totalRow] : nonTotal;
  }, [visibleRows, sortDesc]);

  const displayUnits = useMemo(() => {
    const list = [...units];
    const idx = list.indexOf('Total');
    if (idx > -1) {
      list.splice(idx, 1);
      list.push('Total');
    }
    return list;
  }, [units]);

  return (
    <div className={classes.reportContainer}>
      <div className={classes.reportSectionTitle}>Current Active Tasks</div>

      <Grid container spacing={3} className={classes.filtersRow}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <Tooltip
                title={
                  lastRefreshTs
                    ? `Last Update: ${convertToArborDate(lastRefreshTs, true).getUtcDatetime(true)}`
                    : ''
                }
                placement="top"
              >
                <span />
              </Tooltip>
              <Grid container direction="column">
                <Grid item>
                  <span className={classes.filterLabel}>Customer</span>
                </Grid>
                <Grid item>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    options={customerOptions}
                    getOptionLabel={o => o.label}
                    value={customerOptions.filter(o => filters.customerIds.includes(o.id))}
                    onChange={(e, vals) =>
                      setFilters(prev => ({ ...prev, customerIds: vals.map(v => v.id) }))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.label}
                            {...tagProps}
                            size="small"
                            className={`${tagProps.className} ${classes.filterChip}`}
                          />
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="standard"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {!filters.customerIds.length && (
                                <Chip
                                  label="All"
                                  size="small"
                                  style={{ margin: '2px 4px 2px 0' }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
              <Grid container direction="column">
                <Grid item>
                  <span className={classes.filterLabel}>Service Group</span>
                </Grid>
                <Grid item>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    options={serviceGroupOptions}
                    getOptionLabel={o => o.label}
                    value={serviceGroupOptions.filter(o => filters.serviceGroupIds.includes(o.id))}
                    onChange={(e, vals) =>
                      setFilters(prev => ({ ...prev, serviceGroupIds: vals.map(v => v.id) }))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.label}
                            {...tagProps}
                            size="small"
                            className={`${tagProps.className} ${classes.filterChip}`}
                          />
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="standard"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {!filters.serviceGroupIds.length && (
                                <Chip
                                  label="All"
                                  size="small"
                                  style={{ margin: '2px 4px 2px 0' }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
              <Grid container direction="column">
                <Grid item>
                  <span className={classes.filterLabel}>Task Type</span>
                </Grid>
                <Grid item>
                  <Autocomplete
                    multiple
                    options={taskTypes.filter(t => t !== 'Total')}
                    getOptionLabel={t => t}
                    value={filters.taskTypes}
                    filterSelectedOptions
                    onChange={(e, vals) => {
                      setFilters(prev => {
                        const hasAll = vals.includes('All');
                        const hadAll = prev.taskTypes.includes('All');
                        if (hasAll && !hadAll) {
                          return { ...prev, taskTypes: ['All'] };
                        }
                        const cleaned = vals.filter(v => v !== 'All' && v !== 'Total');
                        if (hadAll && cleaned.length) {
                          return { ...prev, taskTypes: cleaned };
                        }
                        return { ...prev, taskTypes: cleaned.length ? cleaned : ['All'] };
                      });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const isAll = option === 'All';
                        return (
                          <Chip
                            key={key}
                            label={option}
                            {...tagProps}
                            size="small"
                            onDelete={isAll ? undefined : tagProps.onDelete}
                            className={`${tagProps.className} ${classes.filterChip}`}
                          />
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField {...params} variant="standard" size="small" />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.generalReportTable}>
            <div className={classes.generalReportTableContainer}>
              <table className={classes.generalReportTableMain}>
                <thead>
                  <tr>
                    <th className={classes.generalReportHeaderTh}>
                      <div className={classes.generalReportHeaderTr}>Task/Customer</div>
                    </th>
                    {displayUnits.map(unit => (
                      <th
                        key={unit}
                        className={
                          unit === 'Total'
                            ? `${classes.generalReportHeaderTh} ${classes.stickyRightHeader}`
                            : classes.generalReportHeaderTh
                        }
                      >
                        <div
                          className={classes.generalReportHeaderTr}
                          onClick={unit === 'Total' ? () => setSortDesc(prev => !prev) : undefined}
                          style={{ cursor: unit === 'Total' ? 'pointer' : 'default' }}
                        >
                          {unit === 'Total' ? (
                            `Total ${sortDesc ? '▼' : '▲'}`
                          ) : (
                            <Tooltip title={customerNames[unit] || ''} placement="top">
                              <span>{unit}</span>
                            </Tooltip>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={classes.generalReportMainTbody}>
                  {sortedRows.map((row, rowIndex) => (
                    <tr
                      key={row.taskType}
                      className={
                        row.taskType === 'Total'
                          ? classes.totalRow
                          : rowIndex % 2 !== 0
                          ? classes.oddRow
                          : undefined
                      }
                    >
                      <td className={`${classes.generalReportTbodyTr} ${classes.firstColumnCell}`}>
                        {row.taskType}
                      </td>
                      {displayUnits.map(unit => (
                        <td
                          key={`${row.taskType}-${unit}`}
                          className={
                            unit === 'Total'
                              ? `${classes.generalReportTbodyTr} ${classes.stickyRightCell} ${
                                  rowIndex % 2 !== 0
                                    ? classes.stickyRightCellOdd
                                    : classes.stickyRightCellEven
                                }`
                              : classes.generalReportTbodyTr
                          }
                        >
                          {row.counts[unit]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

export default withStyles(styles as any, { withTheme: true })(QsReport);
